import { getFeaturePanelDefinition } from "../feature-panel-registry.ts";
import type { RuntimeFeatureModule } from "../runtime-feature-registry.ts";
import {
  applySimplificationEffectValue,
  resetSimplificationEffectValue,
} from "../shared/page-simplification-effects.ts";

const attribute = "data-migrosone-a11y-hide-images";
const imageVariables = [
  "--migrosone-a11y-image-visibility",
  "--migrosone-a11y-image-opacity",
  "--migrosone-a11y-image-filter",
] as const;
const allowedModes = ["hidden", "dimmed", "placeholder"] as const;

type ImageMode = (typeof allowedModes)[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readBooleanDefault(config: unknown) {
  if (!isRecord(config)) {
    return false;
  }

  if (typeof config.enabledByDefault === "boolean") {
    return config.enabledByDefault;
  }

  return typeof config.defaultEnabled === "boolean" ? config.defaultEnabled : false;
}

function readMode(config: unknown): ImageMode {
  const configuredMode =
    isRecord(config) && typeof config.mode === "string" ? config.mode.trim() : "hidden";

  return allowedModes.includes(configuredMode as ImageMode)
    ? (configuredMode as ImageMode)
    : "hidden";
}

function resolveEnabled(config: unknown, preference: unknown) {
  return typeof preference === "boolean" ? preference : readBooleanDefault(config);
}

function resetImageVariables(document: Document) {
  for (const variable of imageVariables) {
    resetSimplificationEffectValue({ attribute, document, variable });
  }
}

function imageStyleForMode(mode: ImageMode) {
  if (mode === "dimmed") {
    return {
      filter: "grayscale(1)",
      opacity: "0.24",
      visibility: "visible",
    };
  }

  if (mode === "placeholder") {
    return {
      filter: "grayscale(1)",
      opacity: "0.08",
      visibility: "visible",
    };
  }

  return {
    filter: "none",
    opacity: "0",
    visibility: "hidden",
  };
}

function applyImageStyle(document: Document, mode: ImageMode) {
  const style = imageStyleForMode(mode);

  applySimplificationEffectValue({
    attribute,
    document,
    value: style.visibility,
    variable: "--migrosone-a11y-image-visibility",
  });
  applySimplificationEffectValue({
    attribute,
    document,
    value: style.opacity,
    variable: "--migrosone-a11y-image-opacity",
  });
  applySimplificationEffectValue({
    attribute,
    document,
    value: style.filter,
    variable: "--migrosone-a11y-image-filter",
  });
}

const definition = getFeaturePanelDefinition("hide-images");

if (!definition) {
  throw new Error("Missing hide-images feature definition");
}

export const hideImagesFeatureModule: RuntimeFeatureModule = {
  key: "hide-images",
  definition,
  isSupported({ document }) {
    return Boolean(document.head && document.body && document.documentElement);
  },
  renderControl({ config, document, onPreferenceChange, preference }) {
    const wrapper = document.createElement("label");
    const input = document.createElement("input");
    const text = document.createElement("span");

    wrapper.className = "migrosone-a11y-widget-feature-control";
    input.type = "checkbox";
    input.checked = resolveEnabled(config, preference);
    text.textContent = "Görselleri gizle";

    input.addEventListener("change", () => {
      onPreferenceChange(input.checked);
    });

    wrapper.append(input, text);
    return wrapper;
  },
  apply({ config, document, preference }) {
    resetImageVariables(document);
    if (!resolveEnabled(config, preference)) {
      return;
    }

    applyImageStyle(document, readMode(config));
  },
  reset({ config, document }) {
    resetImageVariables(document);
    if (readBooleanDefault(config)) {
      applyImageStyle(document, readMode(config));
    }
  },
};
